import { Badge, Flex, Input } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { StatusBadge } from '../../../components/StatusBadge'
import { toDateTimeLocalValue, useAppToast } from '../../../helpers'
import { CoursesOnlineService } from '../../../services/CoursesOnline/CoursesOnlineService'
import { CoursesService } from '../../../services/CoursesService/CoursesService'
import { IBulkPriceSchedule } from '../../../services/CoursesService/interfaces'
import { BULK_MODALITY } from './bulkPriceConstants'
import BulkPriceForm from './BulkPriceForm'
import { buildBulkPayload, isValidBulkForm } from './bulkPricePayload'

interface IEditBulkScheduleModalProps {
  schedule: IBulkPriceSchedule | null
  isOpen: boolean
  onClose: () => void
  onSaved: () => void
}

const MODE_LABELS: Record<string, string> = {
  discount: 'Desconto (%)',
  prices: 'Alterar Preços',
  coupon: 'Cupom'
}

export default function EditBulkScheduleModal({ schedule, isOpen, onClose, onSaved }: IEditBulkScheduleModalProps) {
  const coursesService = new CoursesService()
  const recordedService = new CoursesOnlineService()
  const toast = useAppToast()

  const [courses, setCourses] = useState<{ id: number; name: string }[]>([])
  const [loadingCourses, setLoadingCourses] = useState(false)

  // Editable fields
  const [scheduledAt, setScheduledAt] = useState('')
  const [selectedCourseIds, setSelectedCourseIds] = useState<number[]>([])

  // Price fields
  const [discount, setDiscount] = useState('')
  const [taxaMatricula, setTaxaMatricula] = useState<number | null>(null)
  const [priceOriginal, setPriceOriginal] = useState<number | null>(null)

  // Coupon fields
  const [removeCoupon, setRemoveCoupon] = useState(false)
  const [couponValue, setCouponValue] = useState('')

  // CTA text field
  const [ctaText, setCtaText] = useState('')

  const [saving, setSaving] = useState(false)

  // Pre-fill form when schedule changes
  useEffect(() => {
    if (!schedule || !isOpen) return

    // Format scheduled_at for datetime-local input (strip seconds if present)
    setScheduledAt(schedule.scheduled_at.slice(0, 16))

    const payload = schedule.payload

    // Load course list for the modality
    setLoadingCourses(true)
    const load =
      schedule.modality === 'recorded'
        ? recordedService.listForSelect()
        : coursesService.listForSelect(
            schedule.modality === 'in_person' ? true : undefined,
            schedule.modality === 'live_online' ? true : undefined,
            undefined
          )

    load
      .then((data) => {
        setCourses(data)

        // Pre-select courses from payload
        if (schedule.mode === 'coupon') {
          const ids = (payload.course_ids as number[] | undefined) ?? []
          setSelectedCourseIds(ids)
        } else {
          const entries = (payload.entries as { course_id: number }[] | undefined) ?? []
          setSelectedCourseIds(entries.map((e) => e.course_id))
        }
      })
      .catch(() => {
        toast.error('Erro ao carregar cursos')
      })
      .finally(() => setLoadingCourses(false))

    // Pre-fill price/coupon fields
    if (schedule.mode === 'coupon') {
      const coupon = payload.coupon ?? null
      if (coupon === null || coupon === '') {
        setRemoveCoupon(true)
        setCouponValue('')
      } else {
        setRemoveCoupon(false)
        setCouponValue(coupon as string)
      }
      setDiscount('')
      setTaxaMatricula(null)
      setPriceOriginal(null)
    } else {
      setDiscount(payload.discount !== undefined ? String(payload.discount) : '')
      setTaxaMatricula(payload.taxa_matricula !== undefined ? Number(payload.taxa_matricula) : null)
      setPriceOriginal(payload.price_original !== undefined ? Number(payload.price_original) : null)
      setRemoveCoupon(false)
      setCouponValue('')
    }

    // Pre-fill CTA text
    setCtaText((payload.cta_text as string) ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedule, isOpen])

  if (!schedule) return null

  const isValid = isValidBulkForm({
    mode: schedule.mode,
    selectedCourseIds,
    discount,
    taxaMatricula,
    priceOriginal,
    removeCoupon,
    couponValue,
    isScheduled: true,
    scheduledAt
  })

  const handleSave = async () => {
    if (!isValid) return
    setSaving(true)
    try {
      const payload = buildBulkPayload({
        mode: schedule.mode,
        selectedCourseIds,
        modality: schedule.modality,
        discount,
        taxaMatricula,
        priceOriginal,
        removeCoupon,
        couponValue,
        ctaText
      })

      await coursesService.updateBulkPriceSchedule(schedule.id, {
        scheduled_at: scheduledAt,
        payload
      })

      toast.success('Agendamento atualizado!')
      onSaved()
      onClose()
    } catch {
      toast.error('Erro ao salvar', 'Não foi possível atualizar o agendamento.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Editar Agendamento"
      onSubmit={handleSave}
      confirmLabel="Salvar alterações"
      isLoading={saving}
      isConfirmDisabled={!isValid}
    >
      {/* Read-only info */}
      <Flex gap={2} mb={5}>
        <StatusBadge status={schedule.modality} statuses={BULK_MODALITY} py={1} />
        <Badge colorScheme="purple" py={1}>
          {MODE_LABELS[schedule.mode]}
        </Badge>
      </Flex>

      {/* Scheduled date/time */}
      <FormField label="Data e hora da alteração" mb={5}>
        <Input
          type="datetime-local"
          value={scheduledAt}
          min={toDateTimeLocalValue()}
          onChange={(e) => setScheduledAt(e.target.value)}
        />
      </FormField>

      {/* Course selection + mode-specific fields + CTA */}
      <BulkPriceForm
        mode={schedule.mode}
        courses={courses}
        loadingCourses={loadingCourses}
        selectedCourseIds={selectedCourseIds}
        onCourseIdsChange={setSelectedCourseIds}
        coursePlaceholder="Selecione os cursos…"
        discount={discount}
        onDiscountChange={setDiscount}
        taxaMatricula={taxaMatricula}
        onTaxaMatriculaChange={setTaxaMatricula}
        priceOriginal={priceOriginal}
        onPriceOriginalChange={setPriceOriginal}
        removeCoupon={removeCoupon}
        onRemoveCouponChange={setRemoveCoupon}
        couponValue={couponValue}
        onCouponValueChange={setCouponValue}
        ctaText={ctaText}
        onCtaTextChange={setCtaText}
        compact
      />
    </FormModal>
  )
}
